import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import PanelCard from '../../performance-operations/components/PanelCard';
import { Badge, Clickable, Sub, Table } from '../../performance-operations/styles';
import { formatNumber } from '../../performance-operations/config';
import { Chips, HeatCell, HeatTable } from '../styles';
import {
  axisLabel, transitionCount, transitionRows,
} from '../config';

// Analiz E09 alt sol — önceki dönemden hücre geçiş matrisi; yalnız iki süreçte de yerleşimi olan kişiler. Satır = önceki süreç
// hücresi, sütun = seçili süreç hücresi. n = hücre toplamı (başlıkta); yalnız tek süreçte yerleşimi olanlar ayrı sayılır.
// Isı tablosu grafik görünümüdür; "Tablo Olarak Göster" sıfır olmayan geçişleri büyükten küçüğe sıralar (klavyeyle detaya iniş).
export default function TransitionPanel({ panel, selection, onSelect }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [table, setTable] = useState(false);
  const data = panel.data;
  const visible = data?.potentialVisible !== false;
  const keys = data?.keys || [];
  const max = Math.max(1, ...(data?.cells || []).map((c) => c.count || 0));
  const selected = selection?.type === 'transition' ? `${selection.from}→${selection.to}` : null;
  const pick = (c) => { if (!c.suppressed && (c.count || 0) > 0) onSelect({ type: 'transition', from: c.from, to: c.to }); };
  const noPair = !!data && (!data.process || !data.previousProcess);
  const short = (key) => axisLabel(t, key, visible);

  return (
    <PanelCard
      panelKey="transitions" title={t('talent.nineBoxTransitions.panel.transitions')} span={8}
      loading={panel.loading} error={panel.error} onRetry={panel.retry}
      empty={noPair || (!!data && data.n === 0)}
      emptyText={noPair ? t('talent.nineBoxTransitions.noPreviousProcess') : t('talent.nineBoxTransitions.noTransitions')}
      tableToggle showTable={table} onToggleTable={() => setTable((v) => !v)}
      actions={data && !noPair ? (
        <Sub data-meta="transition-n">
          {t('talent.nineBoxTransitions.transitionN', { n: data.n, currentOnly: data.currentOnly, previousOnly: data.previousOnly })}
        </Sub>
      ) : null}
    >
      {table ? (
        <Table>
          <thead>
            <tr>
              <th>{t('talent.nineBoxTransitions.col.from')}</th>
              <th>{t('talent.nineBoxTransitions.col.to')}</th>
              <th>{t('talent.nineBoxTransitions.col.count')}</th>
            </tr>
          </thead>
          <tbody>
            {transitionRows(data?.cells).map((c) => (
              <tr key={`${c.from}→${c.to}`} data-row={`${c.from}→${c.to}`}>
                <td>{short(c.from)}</td>
                <td>
                  {c.suppressed ? short(c.to) : (
                    <Clickable type="button" data-pick={`${c.from}→${c.to}`} onClick={() => pick(c)}>{short(c.to)}</Clickable>
                  )}
                </td>
                <td>{c.suppressed ? t('talent.nineBoxTransitions.suppressedCell') : formatNumber(c.count, lang, 0)}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      ) : (
        <HeatTable>
          <thead>
            <tr>
              <th scope="col">{t('talent.nineBoxTransitions.col.fromTo')}</th>
              {keys.map((k) => <th key={k} scope="col" title={short(k)}>{short(k)}</th>)}
            </tr>
          </thead>
          <tbody>
            {keys.map((from) => (
              <tr key={from}>
                <th scope="row" title={short(from)}>{short(from)}</th>
                {keys.map((to) => {
                  const c = transitionCount(data?.cells, from, to) || { from, to, count: 0, suppressed: false };
                  const id = `${from}→${to}`;
                  const disabled = c.suppressed || (c.count || 0) === 0;
                  return (
                    <td key={to}>
                      <HeatCell
                        type="button" data-transition={id} disabled={disabled} $diagonal={from === to}
                        $tone={c.suppressed ? 0 : (c.count || 0) / max}
                        aria-pressed={selected === id}
                        aria-label={`${short(from)} → ${short(to)}: ${c.suppressed ? t('talent.nineBoxTransitions.suppressedCell') : c.count}`}
                        onClick={() => pick(c)}
                      >
                        {c.suppressed ? '•' : formatNumber(c.count, lang, 0)}
                      </HeatCell>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </HeatTable>
      )}
      {data?.movement?.length ? (
        <Chips data-movement="1">
          {data.movement.map((m) => (
            <Badge key={m.key} $tone={m.key.endsWith('Up') ? 'success' : m.key.endsWith('Down') ? 'danger' : 'info'} data-move={m.key}>
              {`${t(`talent.nineBoxTransitions.movement.${m.key}`)}: ${m.suppressed ? t('talent.nineBoxTransitions.suppressedCell') : formatNumber(m.count, lang, 0)}`}
            </Badge>
          ))}
        </Chips>
      ) : null}
      {data?.cells?.some((c) => c.suppressed) ? <Sub data-note="suppressed">{t('talent.nineBoxTransitions.suppressedNote', { k: data.threshold })}</Sub> : null}
    </PanelCard>
  );
}
